import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Tooltip
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Save,
  Refresh,
  Visibility,
  Settings,
  Build,
  CloudUpload
} from '@mui/icons-material';

const DevView: React.FC = () => {
  const [devMode, setDevMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [config, setConfig] = useState({
    appTitle: 'NRPSA Score - Property Compliance Health Check',
    maxStars: 5,
    enableZeroStar: true,
    primaryColor: '#1976d2',
    secondaryColor: '#f57c00',
    logoPath: '/images/National-Residential-Property-Standards-Award.png'
  });

  const handleConfigChange = (field: string, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const saveConfig = () => {
    console.log('Saving configuration:', config);
    // Here you would implement actual save functionality
    setEditMode(false);
  };

  const deployApp = () => {
    console.log('Deploying application...');
    // This would trigger a deployment
  };

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" color="white" gutterBottom>
                🛠️ NRPSA Score - Developer Dashboard
              </Typography>
              <Typography variant="subtitle1" color="white" sx={{ opacity: 0.9 }}>
                Make real-time amendments to your property compliance application
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Toggle Development Mode">
                <FormControlLabel
                  control={
                    <Switch
                      checked={devMode}
                      onChange={(e) => setDevMode(e.target.checked)}
                      sx={{ '& .MuiSwitch-thumb': { color: 'white' } }}
                    />
                  }
                  label={<Typography color="white">Dev Mode</Typography>}
                />
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {devMode && (
        <>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🚀 Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Visibility />}
                    href="https://score.nrpsa.org"
                    target="_blank"
                  >
                    View Live Site
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditMode(!editMode)}
                  >
                    {editMode ? 'Cancel Edit' : 'Edit Config'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CloudUpload />}
                    onClick={deployApp}
                  >
                    Deploy Changes
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Configuration Panel */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">⚙️ Application Configuration</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Application Title"
                    value={config.appTitle}
                    onChange={(e) => handleConfigChange('appTitle', e.target.value)}
                    disabled={!editMode}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Logo Path"
                    value={config.logoPath}
                    onChange={(e) => handleConfigChange('logoPath', e.target.value)}
                    disabled={!editMode}
                    variant="outlined"
                    margin="normal"
                    helperText="Path to your logo file in /public/"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Maximum Stars"
                    type="number"
                    value={config.maxStars}
                    onChange={(e) => handleConfigChange('maxStars', parseInt(e.target.value))}
                    disabled={!editMode}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Primary Color"
                    value={config.primaryColor}
                    onChange={(e) => handleConfigChange('primaryColor', e.target.value)}
                    disabled={!editMode}
                    variant="outlined"
                    margin="normal"
                    type="color"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Secondary Color"
                    value={config.secondaryColor}
                    onChange={(e) => handleConfigChange('secondaryColor', e.target.value)}
                    disabled={!editMode}
                    variant="outlined"
                    margin="normal"
                    type="color"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enableZeroStar}
                        onChange={(e) => handleConfigChange('enableZeroStar', e.target.checked)}
                        disabled={!editMode}
                      />
                    }
                    label="Enable Zero-Star Rating (Urgent Non-Habitable)"
                  />
                </Grid>
              </Grid>
              {editMode && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={saveConfig}
                  >
                    Save Configuration
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setEditMode(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Content Management */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">📝 Content Management</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Assessment Questions
                      </Typography>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Manage the property compliance questions
                      </Typography>
                      <Button variant="outlined" startIcon={<Edit />}>
                        Edit Questions
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Scoring System
                      </Typography>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Configure star ratings and thresholds
                      </Typography>
                      <Button variant="outlined" startIcon={<Settings />}>
                        Configure Scoring
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* System Status */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">📊 System Status</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Alert severity="success">
                    <Typography variant="subtitle2">Frontend Status</Typography>
                    <Typography variant="body2">✅ Deployed & Running</Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Alert severity="warning">
                    <Typography variant="subtitle2">Backend API</Typography>
                    <Typography variant="body2">⚠️ Configuration Needed</Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Alert severity="info">
                    <Typography variant="subtitle2">Database</Typography>
                    <Typography variant="body2">ℹ️ Azure Cosmos DB Ready</Typography>
                  </Alert>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Recent Deployments */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">🚀 Recent Deployments</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip label="LIVE" color="success" size="small" />
                  <Typography variant="body2">
                    https://y-h5caqcmwb-peter-savages-projects-a80ca9a0.vercel.app
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    2 hours ago
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip label="PREVIOUS" color="default" size="small" />
                  <Typography variant="body2">
                    https://y-rm8go01dc-peter-savages-projects-a80ca9a0.vercel.app
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    4 hours ago
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {!devMode && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h5" gutterBottom>
              🔒 Development Mode Disabled
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Toggle the "Dev Mode" switch above to access development tools and configuration options.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setDevMode(true)}
              startIcon={<Build />}
            >
              Enable Developer Mode
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DevView;